import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { useToast } from '@/contexts/admin/ToastContext';
import {
  usePutCreditCardMutation,
  usePutBankAccountMutation,
  useDeleteCreditCardMutation,
  useDeleteBankAccountMutation,
  useGetPaymentMethodsQuery,
} from '@/store/api';
import { PaymentMethodCard } from '@/components/residents/payments/PaymentMethodCard';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import type { PaymentMethod } from '@/types/residents/payment';

const EmptyCardIcon = RESIDENTS_SVG_COMPONENTS.EMPTY_CARD;

interface ManageWalletContentProps {
  paymentMethods: PaymentMethod[];
  onMethodsUpdated: () => void;
}

/**
 * Manage Wallet Content - Displays and manages saved payment methods
 */
export const ManageWalletContent: React.FC<ManageWalletContentProps> = ({
  paymentMethods,
  onMethodsUpdated,
}) => {
  const { showSuccess, showError } = useToast();
  const { refetch } = useGetPaymentMethodsQuery();

  // Mutations
  const [setCardDefault, { isLoading: settingCardDefault }] = usePutCreditCardMutation();
  const [setBankDefault, { isLoading: settingBankDefault }] = usePutBankAccountMutation();
  const [deleteCard, { isLoading: deletingCard }] = useDeleteCreditCardMutation();
  const [deleteBank, { isLoading: deletingBank }] = useDeleteBankAccountMutation();

  // Delete confirmation state
  const [methodToDelete, setMethodToDelete] = useState<PaymentMethod | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isLoading = settingCardDefault || settingBankDefault || deletingCard || deletingBank;

  const handleSetDefault = useCallback(async (method: PaymentMethod) => {
    try {
      const sourceId = method.sourceId || method.id;
      
      if (method.type === 'card') {
        await setCardDefault({
          sourceId,
          creditCard: { isDefault: true },
        }).unwrap();
      } else {
        await setBankDefault({
          sourceId,
          bankAccount: { isDefault: true },
        }).unwrap();
      }

      showSuccess('Default payment method updated');
      refetch();
    } catch (error) {
      showError('Failed to update default payment method');
    }
  }, [setCardDefault, setBankDefault, showSuccess, showError, refetch]);

  const handleDeletePress = useCallback((method: PaymentMethod) => {
    setMethodToDelete(method);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!methodToDelete) return;

    try {
      const sourceId = methodToDelete.sourceId || methodToDelete.id;

      if (methodToDelete.type === 'card') {
        await deleteCard({ sourceId }).unwrap();
      } else {
        await deleteBank({ sourceId }).unwrap();
      }

      showSuccess('Payment method removed');
      setShowDeleteConfirm(false);
      setMethodToDelete(null);
      refetch();
      onMethodsUpdated();
    } catch (error) {
      showError('Failed to remove payment method');
      setShowDeleteConfirm(false);
      setMethodToDelete(null);
    }
  }, [methodToDelete, deleteCard, deleteBank, showSuccess, showError, refetch, onMethodsUpdated]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
    setMethodToDelete(null);
  }, []);

  const isEmpty = paymentMethods.length === 0;
  const isOnlyCard = paymentMethods.length === 1;

  // Render payment methods grid or single card
  const renderPaymentMethods = () => {
    if (isOnlyCard) {
      const method = paymentMethods[0];
      return (
        <View style={styles.singleCardContainer}>
          <PaymentMethodCard
            method={null}
            variant="wallet"
            index={0}
            type={method.type === 'card' ? 'card' : 'ach'}
            last4={method.last4}
            brand={method.brand}
            bankName={method.bankName}
            expiryMonth={method.expiryMonth}
            expiryYear={method.expiryYear}
            isDefault={method.isDefault}
            isExpired={method.isExpired}
            isOnlyCard={true}
            onSetAsDefault={() => handleSetDefault(method)}
            onDelete={() => handleDeletePress(method)}
            disabled={isLoading}
            showActions={true}
          />
        </View>
      );
    }
    
    return (
      <View style={styles.methodsGrid}>
        {/* Left column */}
        <View style={styles.column}>
          {paymentMethods
            .filter((_, index) => index % 2 === 0)
            .map((method, idx) => (
              <PaymentMethodCard
                key={method.id}
                method={null}
                variant="wallet"
                index={idx * 2}
                type={method.type === 'card' ? 'card' : 'ach'}
                last4={method.last4}
                brand={method.brand}
                bankName={method.bankName}
                expiryMonth={method.expiryMonth}
                expiryYear={method.expiryYear}
                isDefault={method.isDefault}
                isExpired={method.isExpired}
                isOnlyCard={false}
                onSetAsDefault={() => handleSetDefault(method)}
                onDelete={() => handleDeletePress(method)}
                disabled={isLoading}
                showActions={true}
              />
            ))}
        </View>

        {/* Right column */}
        <View style={styles.column}>
          {paymentMethods
            .filter((_, index) => index % 2 === 1)
            .map((method, idx) => (
              <PaymentMethodCard
                key={method.id}
                method={null}
                variant="wallet"
                index={idx * 2 + 1}
                type={method.type === 'card' ? 'card' : 'ach'}
                last4={method.last4}
                brand={method.brand}
                bankName={method.bankName}
                expiryMonth={method.expiryMonth}
                expiryYear={method.expiryYear}
                isDefault={method.isDefault}
                isExpired={method.isExpired}
                isOnlyCard={false}
                onSetAsDefault={() => handleSetDefault(method)}
                onDelete={() => handleDeletePress(method)}
                disabled={isLoading}
                showActions={true}
              />
            ))}
        </View>
      </View>
    );
  };

  return (
    <>
      <View style={styles.container}>
        <View style={styles.content}>
          {isEmpty ? (
            <View style={styles.emptyState}>
              {EmptyCardIcon && (
                <EmptyCardIcon width={94} height={69} color="#757575" />
              )}
              <View style={styles.emptyStateTextContainer}>
                <Text style={styles.emptyStateTitle}>
                  No saved payment methods
                </Text>
                <Text style={styles.emptyStateSubtext}>
                  You'll need to add a new payment method before making your next payment.
                </Text>
              </View>
            </View>
          ) : (
            renderPaymentMethods()
          )}
        </View>
      </View>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={showDeleteConfirm}
        message="Remove saved payment method?"
        onCancel={handleDeleteCancel}
        onProceed={handleDeleteConfirm}
        cancelText="No, cancel"
        proceedText="Proceed"
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  methodsGrid: {
    flexDirection: 'row',
    gap: 16,
  },
  column: {
    flex: 1,
    gap: 16,
  },
  singleCardContainer: {
    width: '100%',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
    gap: 18,
  },
  emptyStateTextContainer: {
    alignItems: 'center',
    gap: 6,
    width: 240,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontSize: 14,
    fontWeight: '400',
    color: '#919191',
    textAlign: 'center',
    lineHeight: 21,
  },
});

export default ManageWalletContent;

